

import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Heading,
  Input,
  Button,
  VStack,
  Text,
  useToast,
  Icon,
  Alert,
  AlertIcon,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  Tab,
  Spinner,
  List,
  ListItem,
  ListIcon,
  Flex,
  Divider,
  useBreakpointValue,
  Container, // <-- Imported Container for centralizing content
} from "@chakra-ui/react";
import { CheckCircleIcon, WarningIcon } from "@chakra-ui/icons";
import { FiUserCheck, FiMaximize, FiList, FiCheckCircle } from "react-icons/fi";
import { useListState } from "@mantine/hooks";
import { Html5QrcodeScanner } from "html5-qrcode";

// --- DUMMY DATA / API Placeholder (Unchanged) ---
const markAttendanceInBackend = (studentId) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const studentMap = {
        S1001: "Akash Varma",
        S1003: "Priya Nair",
        S1002: "Sara K.",
      };
      const studentName = studentMap[studentId];

      if (studentId === "S1001" || studentId === "S1003") {
        resolve({
          success: true,
          studentName: studentName,
          message: "Attendance marked successfully.",
        });
      } else if (studentId === "S1002") {
        resolve({
          success: false,
          message: `Attendance already marked for ${studentName}.`,
        });
      } else {
        resolve({
          success: false,
          message: `Student ID ${studentId} not found.`,
        });
      }
    }, 300);
  });
};

let html5QrcodeScanner = null;

export default function StudentAttendanceScan() {
  const [scanId, setScanId] = useState("");
  const [currentStatus, setCurrentStatus] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [attendanceLog, handlers] = useListState([]);

  const inputRef = useRef(null);
  const qrScannerRef = useRef(null);
  const toast = useToast();
  const isMobile = useBreakpointValue({ base: true, md: false });

  useEffect(() => {
    if (activeTab === 0) {
      inputRef.current?.focus();
    }
  }, [activeTab]);

  const processAttendance = async (id) => {
    const studentId = id.trim().toUpperCase();

    if (!studentId) {
      setCurrentStatus({ status: "error", message: "Scan required." });
      return;
    }

    setCurrentStatus(null);
    setIsScanning(true);

    const result = await markAttendanceInBackend(studentId);

    if (result.success) {
      const statusMessage = `${result.studentName} marked present.`;
      setCurrentStatus({
        status: "success",
        message: statusMessage,
      });
      handlers.prepend({
        id: studentId,
        name: result.studentName,
        time: new Date().toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        }),
      });
      toast({
        title: "Attendance Success",
        description: `${result.studentName} marked present.`,
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } else {
      setCurrentStatus({
        status: "error",
        message: result.message,
      });
      toast({
        title: "Attendance Failed",
        description: result.message,
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
    }

    setIsScanning(false);
    if (activeTab === 0) {
      inputRef.current?.focus();
    }
  };

  const handleIdScanSubmit = async (e) => {
    e.preventDefault();
    const idToProcess = scanId;
    setScanId("");
    await processAttendance(idToProcess);
  };

  // QR Scan Logic (Uses html5-qrcode) - Unchanged initialization logic
  useEffect(() => {
    if (activeTab === 1 && qrScannerRef.current) {
      if (!html5QrcodeScanner) {
        html5QrcodeScanner = new Html5QrcodeScanner(
          "qr-reader",
          {
            fps: 10,
            qrbox: { width: 250, height: 250 },
          },
          /* verbose= */ false
        );

        const onScanSuccess = async (decodedText) => {
          html5QrcodeScanner.pause();
          await processAttendance(decodedText);
          html5QrcodeScanner.resume();
        };

        const onScanFailure = (error) => {
          // console.warn(`Code scan error = ${error}`);
        };

        html5QrcodeScanner.render(onScanSuccess, onScanFailure);
      }
    }

    return () => {
      // Cleanup logic is safe here
    };
  }, [activeTab]);

  return (
    // The outer box sets the main background color and minimum height
    <Box p={{ base: 4, md: 6 }} minH="100vh" bg="#FBFBFD">
      {/* --- Container for Centering and Max Width --- */}
      <Container maxW="container.xl" p={0}>
        {/* --- Header & Breadcrumb (Aligned to the container) --- */}
        <VStack align="stretch" spacing={1} mb={6}>
          <h1 fontSize="20px" color="#515B73" display="flex" alignItems="center">
            {/* <Icon as={FiUserCheck} mr={3} color="#2C7A7B" /> */}
            Student Attendance Scanning
          </h1>
          <div className="breadcrumb">
            <Text fontSize="sm" color="gray.500">
              <span>Students</span> /{" "}
              <Text as="span" fontWeight="semibold">
                Attendance
              </Text>
            </Text>
          </div>
        </VStack>

        {/* Attendance Status Panel (Full width within the container) */}
        {currentStatus && (
          <Alert
            status={currentStatus.status === "success" ? "success" : "warning"}
            mb={6}
            borderRadius="md"
            variant="left-accent"
            flexDirection={{ base: "column", md: "row" }}
            shadow="md"
          >
            <AlertIcon />
            <Text fontWeight="bold">{currentStatus.message}</Text>
          </Alert>
        )}

        {/* --- Main Content: Scanning Panel (Left) and Log (Right) --- */}
        {/* Use 'wrap' on smaller screens for stacking, but 'nowrap' on desktop */}
        <Flex
          direction={{ base: "column", lg: "row" }}
          gap={6}
          align="start"
          flexWrap="wrap"
        >
          {/* 1. Scanning Panel (Card) */}
          <VStack
            flex={{ base: "1 1 100%", lg: "1 1 480px" }} // Full width on mobile, max width on desktop
            maxW={{ lg: "480px" }}
            align="stretch"
            p={6}
            bg="white"
            boxShadow="lg" // Slightly stronger shadow
            borderRadius="xl"
            border="1px solid"
            borderColor="gray.100"
            minH="450px" // Ensure minimum height for visual balance
          >
            <Heading size="md" color="#515B73" mb={4} textAlign="center">
              Select Scanning Method
            </Heading>

            <Tabs
              isFitted
              variant="soft-rounded" // Visually attractive tab style
              colorScheme="teal"
              onChange={(index) => {
                setActiveTab(index);
                setCurrentStatus(null);
              }}
            >
              <TabList mb={4}>
                <Tab fontSize="sm">
                  <Icon as={FiMaximize} mr={2} /> ID/Barcode Scan
                </Tab>
                <Tab fontSize="sm">
                  <Icon as={FiCheckCircle} mr={2} /> QR Code Scan
                </Tab>
              </TabList>
              <TabPanels>
                {/* --- ID/Barcode Scan Panel --- */}
                <TabPanel p={4}>
                  <Box as="form" onSubmit={handleIdScanSubmit}>
                    <Input
                      ref={inputRef}
                      type="text"
                      value={scanId}
                      onChange={(e) => setScanId(e.target.value.toUpperCase())}
                      placeholder="Scan or Enter Student ID (e.g., S1001)"
                      size="lg"
                      fontSize="xl"
                      textAlign="center"
                      autoFocus={activeTab === 0}
                      isDisabled={isScanning}
                      variant="filled"
                      focusBorderColor="#2C7A7B"
                    />

                    <Button
                      type="submit"
                      colorScheme="teal"
                      size="lg"
                      w="full"
                      mt={4}
                      isLoading={isScanning}
                      loadingText="Processing..."
                      backgroundColor="#2C7A7B"
                      _hover={{ bg: "#256a6b" }}
                    >
                      Mark Present (Manual)
                    </Button>
                  </Box>
                </TabPanel>

                {/* --- QR Code Scan Panel --- */}
                <TabPanel p={4}>
                  <Text mb={4} color="gray.600" textAlign="center">
                    Hold the student's ID card QR code to the camera.
                  </Text>
                  <Box
                    id="qr-reader"
                    ref={qrScannerRef}
                    minH="300px"
                    bg="blackAlpha.100"
                    borderRadius="md"
                    overflow="hidden"
                  >
                    {/* Scanner UI will be rendered here */}
                  </Box>
                  {isScanning && (
                    <Flex justify="center" align="center" mt={4}>
                      <Spinner size="sm" mr={2} color="teal.500" />
                      <Text color="teal.600">Processing Scan...</Text>
                    </Flex>
                  )}
                </TabPanel>
              </TabPanels>
            </Tabs>
          </VStack>

          {/* 2. Attendance Log Panel (Card) */}
          <VStack
            flex="1" // Takes up remaining space
            maxW={{ lg: "400px", base: "100%" }}
            align="stretch"
            p={6}
            bg="white"
            boxShadow="lg"
            borderRadius="xl"
            border="1px solid"
            borderColor="gray.100"
          >
            <Heading size="md" color="#515B73">
              <Icon as={FiList} mr={2} /> Recent Attendance Log
            </Heading>
            <Divider my={2} />
            <Box maxH="400px" overflowY="auto" pt={2}>
              {attendanceLog.length === 0 ? (
                <Text
                  color="gray.500"
                  fontStyle="italic"
                  textAlign="center"
                  py={4}
                >
                  No attendance marked yet.
                </Text>
              ) : (
                <List spacing={3}>
                  {attendanceLog.slice(0, 10).map((log, index) => (
                    <ListItem
                      key={log.id + log.time + index}
                      pb={2}
                      _notLast={{
                        borderBottom: "1px solid",
                        borderColor: "gray.100",
                      }}
                    >
                      <Flex justify="space-between" align="center">
                        <Flex align="center">
                          <ListIcon
                            as={FiCheckCircle}
                            color="green.500"
                            boxSize={5}
                          />
                          <Box ml={2}>
                            <Text
                              fontWeight="semibold"
                              fontSize="md"
                              color="gray.700"
                            >
                              {log.name}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              ID: {log.id}
                            </Text>
                          </Box>
                        </Flex>
                        <Text
                          fontSize="xs"
                          color="gray.400"
                          whiteSpace="nowrap"
                        >
                          {log.time}
                        </Text>
                      </Flex>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </VStack>
        </Flex>
      </Container>
    </Box>
  );
}
